import React from "react";
import { useNavigate } from "react-router-dom";
import { FiUser } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext.jsx";

const STYLE_EXAMPLES = [
  { label: "LOGO", description: "Clean brand logos from rough sketches", image: "/examples/example_logo.jpg" },
  { label: "SPRITE", description: "Game-ready 2D character sprites", image: "/examples/example_sprite.jpg" },
  { label: "PIXEL ART", description: "Retro-style pixel art assets", image: "/examples/example_pixel_art.jpg" },
  { label: "ICON", description: "Minimal flat icons for apps", image: "/examples/example_icon.jpg" },
  { label: "STICKER", description: "Fun die-cut sticker designs", image: "/examples/example_sticker.jpg" },
  { label: "ILLUSTRATION", description: "Polished digital illustrations", image: "/examples/example_illustration.jpg" },
];

const STEPS = [
  {
    number: "01",
    title: "Sketch your idea",
    description:
      "Draw on the canvas with your mouse, finger, or Apple Pencil. You can also drag & drop an image to draw on top of. It doesn't need to be perfect.",
  },
  {
    number: "02",
    title: "Choose a style",
    description:
      "Pick from logo, sprite, pixel art, icon, sticker, or illustration presets.",
  },
  {
    number: "03",
    title: "Generate & download",
    description:
      "AI transforms your sketch into a polished, production-ready asset. Download as PNG.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#fff",
        color: "#111",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Nav */}
      <nav
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px clamp(20px, 5vw, 40px)",
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: "18px", letterSpacing: "-0.02em" }}>
          scribbledraw.
        </div>
        <div className="landing-nav-links">
          <a
            href="#how-it-works"
            style={{
              color: "#666",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            HOW IT WORKS
          </a>
          <a
            href="#styles"
            style={{
              color: "#666",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            STYLES
          </a>
        </div>
        <button
          onClick={() => navigate("/profile")}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "36px",
            height: "36px",
            backgroundColor: "#f5f5f5",
            border: "none",
            borderRadius: "9999px",
            cursor: "pointer",
            color: "#555",
          }}
        >
          <FiUser size={18} />
        </button>
      </nav>

      {/* Hero */}
      <section
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "60px clamp(20px, 5vw, 40px) 80px",
        }}
      >
        <p
          style={{
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.1em",
            color: "#999",
            marginBottom: "20px",
          }}
        >
          AI-POWERED ASSET GENERATOR
        </p>
        <h1
          style={{
            fontSize: "clamp(40px, 6vw, 72px)",
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: "-0.03em",
            marginBottom: "24px",
            maxWidth: "700px",
          }}
        >
          Turn Sketches Into
          <br />
          Real Assets
        </h1>
        <p
          style={{
            fontSize: "18px",
            color: "#666",
            lineHeight: 1.6,
            maxWidth: "480px",
            marginBottom: "32px",
          }}
        >
          Sketch your idea on the canvas. Pick a style. Get a polished, production-ready
          asset in seconds — logos, sprites, icons, and more.
        </p>

        {/* Pill badges */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "40px" }}>
          {["GAME SPRITES", "LOGOS", "PIXEL ART", "ICONS", "STICKERS"].map(
            (badge) => (
              <span
                key={badge}
                style={{
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 500,
                  letterSpacing: "0.05em",
                  color: "#555",
                  border: "1px solid #ddd",
                  borderRadius: "9999px",
                }}
              >
                {badge}
              </span>
            )
          )}
          <span
            style={{
              padding: "6px 14px",
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.05em",
              color: "#fff",
              backgroundColor: "#000",
              borderRadius: "9999px",
            }}
          >
            TRY FOR FREE
          </span>
        </div>

        {/* CTA */}
        <button
          onClick={() => navigate("/app")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            padding: "16px 36px",
            fontSize: "14px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            color: "#fff",
            backgroundColor: "#000",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
          }}
        >
          START CREATING FREE
          <span style={{ fontSize: "18px", lineHeight: 1 }}>&rarr;</span>
        </button>
      </section>

      {/* Styles Grid */}
      <section
        id="styles"
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "60px clamp(20px, 5vw, 40px)",
          borderTop: "1px solid #eee",
        }}
      >
        <p
          style={{
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.1em",
            color: "#999",
            marginBottom: "16px",
          }}
        >
          STYLES
        </p>
        <h2
          style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            marginBottom: "48px",
          }}
        >
          One sketch, any style
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "24px",
          }}
        >
          {STYLE_EXAMPLES.map((style) => (
            <div
              key={style.label}
              style={{
                padding: "32px",
                borderRadius: "12px",
                border: "1px solid #eee",
                transition: "border-color 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = "#ccc")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = "#eee")
              }
            >
              <img
                src={style.image}
                alt={`${style.label} example`}
                style={{
                  width: "100%",
                  height: "160px",
                  objectFit: "contain",
                  backgroundColor: "#fafafa",
                  borderRadius: "8px",
                  marginBottom: "16px",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  color: "#333",
                  marginBottom: "4px",
                }}
              >
                {style.label}
              </p>
              <p style={{ fontSize: "14px", color: "#888", lineHeight: 1.5 }}>
                {style.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section
        id="how-it-works"
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "60px clamp(20px, 5vw, 40px)",
          borderTop: "1px solid #eee",
        }}
      >
        <p
          style={{
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.1em",
            color: "#999",
            marginBottom: "16px",
          }}
        >
          HOW IT WORKS
        </p>
        <h2
          style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            marginBottom: "48px",
          }}
        >
          Three steps. That's it.
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "40px",
          }}
        >
          {STEPS.map((step) => (
            <div key={step.number}>
              <p
                style={{
                  fontSize: "48px",
                  fontWeight: 800,
                  color: "#eee",
                  lineHeight: 1,
                  marginBottom: "12px",
                }}
              >
                {step.number}
              </p>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  marginBottom: "8px",
                }}
              >
                {step.title}
              </h3>
              <p style={{ fontSize: "15px", color: "#888", lineHeight: 1.6 }}>
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "60px clamp(20px, 5vw, 40px)",
          borderTop: "1px solid #eee",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            marginBottom: "16px",
          }}
        >
          Ready to create?
        </h2>
        <p
          style={{
            fontSize: "16px",
            color: "#888",
            marginBottom: "32px",
          }}
        >
          Try it free. No credit card required.
        </p>
        <button
          onClick={() => navigate("/app")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            padding: "16px 36px",
            fontSize: "14px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            color: "#fff",
            backgroundColor: "#000",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
          }}
        >
          START CREATING FREE
          <span style={{ fontSize: "18px", lineHeight: 1 }}>&rarr;</span>
        </button>
      </section>

      {/* Footer */}
      <footer
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "24px clamp(20px, 5vw, 40px)",
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "13px",
          color: "#aaa",
        }}
      >
        <span>&copy; {new Date().getFullYear()} scribbledraw</span>
        <div style={{ display: "flex", gap: "24px" }}>
          <a href="/privacy" style={{ color: "#aaa", textDecoration: "none" }}>
            Privacy
          </a>
          <a href="/terms" style={{ color: "#aaa", textDecoration: "none" }}>
            Terms
          </a>
        </div>
      </footer>
    </div>
  );
}
